import { InProductTrialTypeEnum } from '@nirvana/shared/dtos/types/in-product-trials';
import { BaseWebComponentTranslator } from '../../../../../../framework/BaseWebComponentTranslator';
import { CardItem, ProductItem } from '../../../../gzTrialHomePage/types';
import {
  AvailableMainProductTrialType,
  AvailableSecondaryProductTrialType,
} from '../../../../types/AvailableProductTrialType';
import { getTranslatedInProductTrialType } from '../../../../utils/InProductTrialType';
import { AvailableInProductTrial } from '../../../types/AvailableInProductTrialsDetails';
import { getCommonDescriptionForInProductTrialItem } from '../gzCommonConfig/gz-common-page-config';

// configurations for available main product trials items in home page
function getMainProductTrialTypeToProductItemConfigMapping(): {
  [type in AvailableMainProductTrialType]: ProductItem;
} {
  const translator = BaseWebComponentTranslator.getInstance();
  const buttonText = translator.translateFn('Learn More');

  const descriptions = getCommonDescriptionForInProductTrialItem(translator);

  return {
    [InProductTrialTypeEnum.OfficeSecurityPack]: {
      type: InProductTrialTypeEnum.OfficeSecurityPack,
      title: getTranslatedInProductTrialType(InProductTrialTypeEnum.OfficeSecurityPack),
      description: descriptions[InProductTrialTypeEnum.OfficeSecurityPack],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.OfficeSecurityPack.toString(),
      imageSrc: 'app/webcomponents/assets/images/trial/bs/bsHeader.jpg',
    },
    [InProductTrialTypeEnum.NextGen]: {
      type: InProductTrialTypeEnum.NextGen,
      title: getTranslatedInProductTrialType(InProductTrialTypeEnum.NextGen),
      description: descriptions[InProductTrialTypeEnum.NextGen],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.NextGen.toString(),
      imageSrc: 'app/webcomponents/assets/images/trial/bsPremium/bsPremiumHeader.png',
    },
    [InProductTrialTypeEnum.NextGenWithReconfigurePopup]: {
      type: InProductTrialTypeEnum.NextGenWithReconfigurePopup,
      title: getTranslatedInProductTrialType(InProductTrialTypeEnum.NextGenWithReconfigurePopup),
      description: descriptions[InProductTrialTypeEnum.NextGenWithReconfigurePopup],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.NextGenWithReconfigurePopup.toString(),
      imageSrc: 'app/webcomponents/assets/images/trial/bsPremium/bsPremiumHeader.png',
    },
    [InProductTrialTypeEnum.Atd]: {
      type: InProductTrialTypeEnum.Atd,
      title: getTranslatedInProductTrialType(InProductTrialTypeEnum.Atd),
      description: descriptions[InProductTrialTypeEnum.Atd],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.Atd.toString(),
      imageSrc: 'app/webcomponents/assets/images/trial/bsEnterprise/bsEnterprise_header.jpeg',
    },
    [InProductTrialTypeEnum.XdrFull]: {
      type: InProductTrialTypeEnum.XdrFull,
      title: getTranslatedInProductTrialType(InProductTrialTypeEnum.XdrFull),
      description: descriptions[InProductTrialTypeEnum.XdrFull],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.XdrFull.toString(),
      imageSrc: 'app/webcomponents/assets/images/trial/xdr/xdrHeader.jpg',
    },
    [InProductTrialTypeEnum.XdrSensors]: {
      type: InProductTrialTypeEnum.XdrSensors,
      title: getTranslatedInProductTrialType(InProductTrialTypeEnum.XdrSensors),
      description: descriptions[InProductTrialTypeEnum.XdrSensors],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.XdrSensors.toString(),
      imageSrc: 'app/webcomponents/assets/images/trial/xdr/xdrHeader.jpg',
    },
  };
}

// configurations for available main product trials items in home page
function getSecondaryProductTrialTypeToProductItemConfigMapping(translator: BaseWebComponentTranslator): {
  [type in AvailableSecondaryProductTrialType]: CardItem;
} {
  const buttonText = translator.translateFn('Learn More');
  const descriptions = getCommonDescriptionForInProductTrialItem(translator);

  return {
    [InProductTrialTypeEnum.PatchManagement]: {
      type: InProductTrialTypeEnum.PatchManagement,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.PatchManagement),
      text: descriptions[InProductTrialTypeEnum.PatchManagement],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.PatchManagement.toString(),
      icon: 'icon-patch_management',
    },
    [InProductTrialTypeEnum.VolumeEncryption]: {
      type: InProductTrialTypeEnum.VolumeEncryption,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.VolumeEncryption),
      text: descriptions[InProductTrialTypeEnum.VolumeEncryption],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.VolumeEncryption.toString(),
      icon: 'icon-encryption',
    },
    [InProductTrialTypeEnum.MobileSecurity]: {
      type: InProductTrialTypeEnum.MobileSecurity,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.MobileSecurity),
      text: descriptions[InProductTrialTypeEnum.MobileSecurity],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.MobileSecurity.toString(),
      icon: 'icon-menu_mobile_sec',
    },
    [InProductTrialTypeEnum.IntegrityMonitor]: {
      type: InProductTrialTypeEnum.IntegrityMonitor,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.IntegrityMonitor),
      text: descriptions[InProductTrialTypeEnum.IntegrityMonitor],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.IntegrityMonitor.toString(),
      icon: 'icon-multifile',
    },
    [InProductTrialTypeEnum.ContainerProtection]: {
      type: InProductTrialTypeEnum.ContainerProtection,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ContainerProtection),
      text: descriptions[InProductTrialTypeEnum.ContainerProtection],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.ContainerProtection.toString(),
      icon: 'icon-container',
    },
    [InProductTrialTypeEnum.StorageProtection]: {
      type: InProductTrialTypeEnum.StorageProtection,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.StorageProtection),
      text: descriptions[InProductTrialTypeEnum.StorageProtection],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.StorageProtection.toString(),
      icon: 'icon-storage_protection',
    },
    [InProductTrialTypeEnum.MdrEnterprise]: {
      type: InProductTrialTypeEnum.MdrEnterprise,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.MdrEnterprise),
      text: descriptions[InProductTrialTypeEnum.MdrEnterprise],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.MdrEnterprise.toString(),
      icon: 'ds-icon-MDR',
    },
    [InProductTrialTypeEnum.Cws]: {
      type: InProductTrialTypeEnum.Cws,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.Cws),
      text: descriptions[InProductTrialTypeEnum.Cws],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.Cws.toString(),
      icon: 'ds-icon-menu_cloud_sec',
    },
    [InProductTrialTypeEnum.ThreatIntel]: {
      type: InProductTrialTypeEnum.ThreatIntel,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
      text: descriptions[InProductTrialTypeEnum.ThreatIntel],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.ThreatIntel.toString(),
      icon: 'ds-icon-intelli_zone',
    },
    [InProductTrialTypeEnum.NadAndEraAddon]: {
      type: InProductTrialTypeEnum.NadAndEraAddon,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.NadAndEraAddon),
      text: descriptions[InProductTrialTypeEnum.NadAndEraAddon],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.NadAndEraAddon.toString(),
      icon: 'ds-icon-menu_risk_management',
    },
    [InProductTrialTypeEnum.WebAccessAndDeviceControlAddon]: {
      type: InProductTrialTypeEnum.WebAccessAndDeviceControlAddon,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.WebAccessAndDeviceControlAddon),
      text: descriptions[InProductTrialTypeEnum.WebAccessAndDeviceControlAddon],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.WebAccessAndDeviceControlAddon.toString(),
      icon: 'ds-icon-entity_IP_domain_DNS_DGA_L',
    },
    [InProductTrialTypeEnum.ComplianceManager]: {
      type: InProductTrialTypeEnum.ComplianceManager,
      cardName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ComplianceManager),
      text: descriptions[InProductTrialTypeEnum.ComplianceManager],
      buttonText: buttonText,
      buttonId: InProductTrialTypeEnum.ComplianceManager.toString(),
      icon: 'ds-icon-menu_compliance_manager',
    },
  };
}

// get main product items configuration for home page
export function getHomePageMainProductItemsConfig(mainProducts: Array<AvailableInProductTrial>): Array<ProductItem> {
  // get product items config
  const mainProductTrialTypeToProductItemConfig = getMainProductTrialTypeToProductItemConfigMapping();

  return mainProducts.map(
    (mainProduct: AvailableInProductTrial) => mainProductTrialTypeToProductItemConfig[mainProduct.type]
  );
}

// get secondary product items configuration for home page
export function getHomePageSecondaryCardItemsConfig(
  secondaryProducts: Array<AvailableInProductTrial>
): Array<CardItem> {
  const translator = BaseWebComponentTranslator.getInstance();

  // get product items config
  const secondaryProductTrialTypeToProductItemConfig =
    getSecondaryProductTrialTypeToProductItemConfigMapping(translator);

  return secondaryProducts.map(secondaryProduct => {
    const secondaryProductConfig = secondaryProductTrialTypeToProductItemConfig[secondaryProduct.type];

    secondaryProductConfig.tagInformation = translator.getYearlyProductType(secondaryProduct.isAddon);

    if (secondaryProduct.canBuy) {
      secondaryProductConfig.canBuy = true;
    }

    return secondaryProductConfig;
  });
}
